import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import type { Role } from "../types";
import { formatRole } from "../utils/formatters";

interface OrgNode { id: number; name: string; job_title: string | null; employee_number: string; manager_id: number | null; role: string; }

export default function OrgChartPage() {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  useEffect(() => { void (async () => { try { const d = await api.get<{ nodes: OrgNode[] }>("/api/org/chart"); setNodes(d.nodes); } catch {} })(); }, []);

  const roots = nodes.filter((n) => !n.manager_id);
  const childrenOf = (id: number) => nodes.filter((n) => n.manager_id === id);

  const renderNode = (node: OrgNode, depth: number): React.ReactNode => (
    <div key={node.id} style={{ marginLeft: depth * 24 }} className="mb-2">
      <div className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pme-blue text-sm font-bold text-white">
          {node.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{node.name}</p>
          <p className="text-xs text-gray-500">{node.job_title ?? formatRole(node.role as Role)} · {node.employee_number}</p>
        </div>
      </div>
      {childrenOf(node.id).map((c) => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">{t("nav.orgChart")}</h1>
      </header>
      <main className="flex-1 overflow-y-auto px-5 pb-24">
        {nodes.length === 0 && <p className="text-sm text-gray-400">{t("common.noData")}</p>}
        {roots.map((r) => renderNode(r, 0))}
      </main>
      <BottomNav />
    </div>
  );
}
